import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiSend, FiEye, FiCalendar, FiXCircle, FiTrash2, FiSearch, FiMessageSquare, FiPlus, FiX, FiUsers, FiUser, FiCheck } from "react-icons/fi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Sidebar from "../Sidebar/Sidebar";
import { SmsApis } from "../../apis/smsApis";
import AdminNavbar from "../Navbars/AdminNavbar.js";

interface Message {
  id: number; sender_id: string | number; content: string; message_type: string; status: string;
  scheduled_at: string | null; created_at: string; sent_at: string | null; total_recipients: number;
  successful_sends: number; failed_sends: number; cost: string; campaign_id: number | null;
  message_template_id: number | null; delivery_status: string; delivery_status_time: string | null;
  sender: { id: number; sender_id: string; status: string }; campaign: any;
}
interface Contact { id: number; first_name: string; last_name: string; phone: string; email: string | null; }
interface Group { id: number; name: string; description: string | null; contact_count: number; }
interface SenderID { id: number; sender_id: string; status: string; }
interface MessageTemplate { id: number; name: string; content: string; message_type: string; variables: string[]; }

const STATUS_CONFIG: Record<string, { bg: string; dot: string; text: string }> = {
  sent:      { bg: "bg-green-50",  dot: "bg-green-500",  text: "text-green-700"  },
  delivered: { bg: "bg-teal-50",   dot: "bg-teal-500",   text: "text-teal-700"   },
  draft:     { bg: "bg-gray-100",  dot: "bg-gray-400",   text: "text-gray-600"   },
  scheduled: { bg: "bg-blue-50",   dot: "bg-blue-500",   text: "text-blue-700"   },
  sending:   { bg: "bg-amber-50",  dot: "bg-amber-500",  text: "text-amber-700"  },
  failed:    { bg: "bg-red-50",    dot: "bg-red-500",    text: "text-red-700"    },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status?.toLowerCase()] ?? STATUS_CONFIG.draft;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold capitalize ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {status}
    </span>
  );
}

function Modal({ open, onClose, children, width = "max-w-xl" }: {
  open: boolean; onClose: () => void; children: React.ReactNode; width?: string;
}) {
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(6px)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`bg-white rounded-3xl shadow-2xl w-full ${width} max-h-[92vh] flex flex-col`}>
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ title, subtitle, onClose }: { title: string; subtitle?: string; onClose: () => void }) {
  return (
    <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 flex-shrink-0">
      <div>
        <h2 className="text-base font-extrabold text-gray-900">{title}</h2>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 text-gray-400 hover:bg-gray-200 transition-colors">
        <FiX size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
}

const inputCls = "w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0071BC]/20 focus:border-[#0071BC] transition-all";
const labelCls = "block text-xs font-bold text-gray-700 mb-1.5";

export default function MessagesManagement() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [fromItem, setFromItem] = useState(0);
  const [toItem, setToItem] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const perPage = 20;

  const [createOpen, setCreateOpen] = useState(false);
  const [selectedSenderId, setSelectedSenderId] = useState<number | string>("");
  const [messageContent, setMessageContent] = useState("");
  const [messageType, setMessageType] = useState("sms");
  const [selectedContacts, setSelectedContacts] = useState<number[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduledTime, setScheduledTime] = useState<Date | null>(new Date());
  const [selectedMessageId, setSelectedMessageId] = useState<number | null>(null);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [groupPickerOpen, setGroupPickerOpen] = useState(false);
  const [groupSearch, setGroupSearch] = useState("");

  const [senderIDs, setSenderIDs] = useState<SenderID[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loadingRefs, setLoadingRefs] = useState(true);

  useEffect(() => { fetchMessages(); }, [currentPage, statusFilter, searchQuery, startDate, endDate]);
  useEffect(() => { fetchRefs(); }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await SmsApis.getMessages(perPage, statusFilter, searchQuery, '', currentPage, startDate, endDate);
      if (res?.data) {
        setMessages(res.data.data); setCurrentPage(res.data.current_page);
        setTotalPages(res.data.last_page); setTotalItems(res.data.total);
        setFromItem(res.data.from || 0); setToItem(res.data.to || 0);
      }
    } catch { toast.error("Failed to fetch messages"); }
    finally { setLoading(false); }
  };

  const fetchRefs = async () => {
    setLoadingRefs(true);
    try {
      const [sRes, cRes, gRes, tRes] = await Promise.all([
        SmsApis.getSenderIds(100, "approved"), SmsApis.getContacts(100),
        SmsApis.getGroups(100), SmsApis.getTemplates(100),
      ]);
      if (sRes?.data) setSenderIDs(sRes.data.data);
      if (cRes?.data) setContacts(cRes.data.data);
      if (gRes?.data) setGroups(gRes.data.data);
      if (tRes?.data) setTemplates(tRes.data.data);
    } catch {}
    finally { setLoadingRefs(false); }
  };

  const resetCreate = () => {
    setSelectedSenderId(""); setMessageContent(""); setMessageType("sms");
    setSelectedContacts([]); setSelectedGroups([]); setSelectedTemplateId(null); setScheduledAt(null);
  };

  const generatePages = () => {
    const max = 5;
    let start = Math.max(1, currentPage - Math.floor(max / 2));
    let end = Math.min(totalPages, start + max - 1);
    if (end - start < max - 1) start = Math.max(1, end - max + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const handleCreateMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSenderId) { toast.error("Please select a sender ID"); return; }
    if (!messageContent) { toast.error("Please enter message content"); return; }
    if (!selectedContacts.length && !selectedGroups.length) { toast.error("Please select at least one contact or group"); return; }
    try {
      const res = await SmsApis.createMessage({
        sender_id: selectedSenderId, content: messageContent, message_type: messageType,
        contacts: selectedContacts, groups: selectedGroups,
        message_template_id: selectedTemplateId,
        scheduled_at: scheduledAt ? scheduledAt.toISOString() : null,
      });
      if (res?.data) { toast.success("Message created"); fetchMessages(); setCreateOpen(false); }
      else toast.error("Failed to create message");
    } catch { toast.error("Failed to create message"); }
  };

  const handleSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!scheduledTime) { toast.error("Please select a time"); return; }
    try {
      const res = await SmsApis.scheduleMessage(selectedMessageId!, scheduledTime.toISOString());
      if (res?.data) { toast.success("Message scheduled"); fetchMessages(); setScheduleOpen(false); }
      else toast.error("Failed to schedule message");
    } catch { toast.error("Failed to schedule message"); }
  };

  const handleSend = async (id: number) => {
    try {
      const res = await SmsApis.sendMessage(id);
      if (res?.data) { toast.success("Message sent"); fetchMessages(); }
      else toast.error("Failed to send message");
    } catch { toast.error("Failed to send message"); }
  };

  const handleCancel = async (id: number) => {
    try {
      const res = await SmsApis.cancelScheduledMessage(id);
      if (res?.data) { toast.success("Schedule cancelled"); fetchMessages(); }
      else toast.error("Failed to cancel schedule");
    } catch { toast.error("Failed to cancel schedule"); }
  };

  const handleDelete = async () => {
    try {
      const res = await SmsApis.deleteMessage(selectedMessage!.id);
      if (res?.data) { toast.success("Message deleted"); fetchMessages(); setDeleteOpen(false); }
      else toast.error("Failed to delete message");
    } catch { toast.error("Failed to delete message"); }
  };

  const toggleContact = (id: number) => setSelectedContacts(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleGroup = (id: number) => setSelectedGroups(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const handleTemplateSelect = (tid: number | null) => {
    setSelectedTemplateId(tid);
    if (tid) { const t = templates.find(x => x.id === tid); if (t) { setMessageContent(t.content); setMessageType(t.message_type); } }
  };

  const filteredContacts = contacts.filter(c =>
    `${c.first_name} ${c.last_name}`.toLowerCase().includes(contactSearch.toLowerCase()) || c.phone.includes(contactSearch));
  const filteredGroups = groups.filter(g => g.name.toLowerCase().includes(groupSearch.toLowerCase()));
  const segments = Math.max(1, Math.ceil(messageContent.length / 160));
  const charPct = Math.min(100, (messageContent.length % 160) / 160 * 100);

  return (
    <>
      <Sidebar title="Messages" />
      <div className="relative md:ml-64 bg-gray-50 min-h-screen">
        <AdminNavbar title="" />
        <div className="px-3 sm:px-5 pt-6 pb-12">

          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <h1 className="text-lg font-extrabold text-gray-900">Messages</h1>
              <p className="text-xs text-gray-400 mt-0.5">Compose and manage your SMS messages</p>
            </div>
            <button onClick={() => { resetCreate(); setCreateOpen(true); }}
              className="flex items-center gap-2 bg-[#0071BC] text-white text-sm font-bold px-4 py-2.5 rounded-2xl hover:bg-[#005fa0] active:scale-95 transition-all shadow-sm">
              <FiPlus size={15} /> New Message
            </button>
          </div>

          {/* Filters */}
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm px-4 py-3.5 mb-4 flex flex-wrap gap-3 items-end">
            <div>
              <label className={labelCls}>Status</label>
              <select className={`${inputCls} py-2 pr-8 min-w-[130px]`} value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
                <option value="">All Statuses</option>
                {['draft','scheduled','sent','delivered','sending','failed'].map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>From</label>
              <input type="date" className={`${inputCls} py-2`} value={startDate} max={endDate || undefined}
                onChange={e => { setStartDate(e.target.value); setCurrentPage(1); }} />
            </div>
            <div>
              <label className={labelCls}>To</label>
              <input type="date" className={`${inputCls} py-2`} value={endDate} min={startDate || undefined}
                onChange={e => { setEndDate(e.target.value); setCurrentPage(1); }} />
            </div>
            {(startDate || endDate) && (
              <button type="button" onClick={() => { setStartDate(''); setEndDate(''); setCurrentPage(1); }}
                className="flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-gray-700 py-2.5">
                <FiX size={13} /> Clear dates
              </button>
            )}
            <div className="flex-1 min-w-[180px]">
              <label className={labelCls}>Search</label>
              <div className="relative">
                <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
                <input type="text" className={`${inputCls} pl-10 py-2`} placeholder="Search messages…"
                  value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }} />
              </div>
            </div>
            {!loading && (
              <div className="w-full text-xs text-gray-500">
                <span className="font-bold text-gray-800">{totalItems.toLocaleString()}</span> message{totalItems === 1 ? '' : 's'}
                {(startDate || endDate) ? ` between ${startDate || 'the beginning'} and ${endDate || 'today'}` : ' in total'}
                {statusFilter ? ` with status "${statusFilter}"` : ''}
              </div>
            )}
          </div>

          {/* Table card */}
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {["Content", "Sender ID", "Recipients", "Cost", "Status", "Created", "Actions"].map(h => (
                      <th key={h} className="px-5 py-3 text-xs font-bold text-gray-400 text-left uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <tr key={i}>
                        {[200, 80, 60, 60, 80, 80, 100].map((w, j) => (
                          <td key={j} className="px-5 py-4"><div className="h-3.5 bg-gray-100 rounded-lg animate-pulse" style={{ width: w }} /></td>
                        ))}
                      </tr>
                    ))
                  ) : messages.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-5 py-16 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center mb-1">
                            <FiMessageSquare className="text-gray-300" size={22} />
                          </div>
                          <p className="text-sm font-semibold text-gray-400">No messages yet</p>
                          <p className="text-xs text-gray-300">Click "New Message" to send your first SMS</p>
                        </div>
                      </td>
                    </tr>
                  ) : messages.map(msg => (
                    <tr key={msg.id} className="hover:bg-gray-50/60 transition-colors">
                      <td className="px-5 py-3.5"><p className="max-w-[220px] truncate text-gray-800 text-sm font-medium">{msg.content}</p></td>
                      <td className="px-5 py-3.5"><span className="font-bold text-gray-700">{msg.sender?.sender_id}</span></td>
                      <td className="px-5 py-3.5 text-gray-500">{msg.total_recipients}</td>
                      <td className="px-5 py-3.5 font-bold text-gray-800">₦{msg.cost}</td>
                      <td className="px-5 py-3.5"><StatusBadge status={msg.status} /></td>
                      <td className="px-5 py-3.5 text-gray-400 text-xs whitespace-nowrap">{new Date(msg.created_at).toLocaleDateString()}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <button onClick={() => { setSelectedMessage(msg); setDetailsOpen(true); }} title="View"
                            className="w-7 h-7 flex items-center justify-center rounded-xl bg-blue-50 text-[#0071BC] hover:bg-blue-100 transition-colors">
                            <FiEye size={13} />
                          </button>
                          {msg.status === 'draft' && <>
                            <button onClick={() => handleSend(msg.id)} title="Send Now"
                              className="w-7 h-7 flex items-center justify-center rounded-xl bg-green-50 text-green-600 hover:bg-green-100 transition-colors">
                              <FiSend size={12} />
                            </button>
                            <button onClick={() => { setSelectedMessageId(msg.id); setScheduledTime(new Date()); setScheduleOpen(true); }} title="Schedule"
                              className="w-7 h-7 flex items-center justify-center rounded-xl bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors">
                              <FiCalendar size={12} />
                            </button>
                          </>}
                          {msg.status === 'scheduled' && (
                            <button onClick={() => handleCancel(msg.id)} title="Cancel Schedule"
                              className="w-7 h-7 flex items-center justify-center rounded-xl bg-orange-50 text-orange-500 hover:bg-orange-100 transition-colors">
                              <FiXCircle size={13} />
                            </button>
                          )}
                          {['draft','scheduled'].includes(msg.status) && (
                            <button onClick={() => { setSelectedMessage(msg); setDeleteOpen(true); }} title="Delete"
                              className="w-7 h-7 flex items-center justify-center rounded-xl bg-red-50 text-red-400 hover:bg-red-100 transition-colors">
                              <FiTrash2 size={12} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!loading && messages.length > 0 && (
              <div className="px-5 py-3.5 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400 flex-wrap gap-3">
                <span>Showing {fromItem}–{toItem} of {totalItems}</span>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}
                    className="px-3 py-1.5 rounded-xl bg-gray-100 text-gray-600 font-bold hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed text-xs">‹</button>
                  {generatePages()[0] > 1 && <>
                    <button onClick={() => setCurrentPage(1)} className="px-3 py-1.5 rounded-xl bg-gray-100 text-gray-600 font-bold hover:bg-gray-200 text-xs">1</button>
                    {generatePages()[0] > 2 && <span className="px-1 text-gray-300">…</span>}
                  </>}
                  {generatePages().map(p => (
                    <button key={p} onClick={() => setCurrentPage(p)}
                      className={`px-3 py-1.5 rounded-xl font-bold text-xs ${p === currentPage ? 'bg-[#0071BC] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      {p}
                    </button>
                  ))}
                  {generatePages().at(-1)! < totalPages && <>
                    {generatePages().at(-1)! < totalPages - 1 && <span className="px-1 text-gray-300">…</span>}
                    <button onClick={() => setCurrentPage(totalPages)} className="px-3 py-1.5 rounded-xl bg-gray-100 text-gray-600 font-bold hover:bg-gray-200 text-xs">{totalPages}</button>
                  </>}
                  <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}
                    className="px-3 py-1.5 rounded-xl bg-gray-100 text-gray-600 font-bold hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed text-xs">›</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Create Message Modal ──────────────────────────────────── */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} width="max-w-lg">
        <ModalHeader title="New Message" subtitle="Compose and send an SMS" onClose={() => setCreateOpen(false)} />
        <form onSubmit={handleCreateMessage} className="overflow-y-auto flex-1 px-6 py-5 space-y-4">

          {/* Sender ID */}
          <div>
            <label className={labelCls}>Sender ID <span className="text-red-400">*</span></label>
            <select className={inputCls} value={selectedSenderId} onChange={e => setSelectedSenderId(e.target.value)} required>
              <option value="">Select Sender ID…</option>
              {senderIDs.map(s => <option key={s.id} value={s.id}>{s.sender_id}</option>)}
            </select>
          </div>

          {/* Template */}
          {templates.length > 0 && (
            <div>
              <label className={labelCls}>Template <span className="text-gray-400 font-normal">(Optional)</span></label>
              <select className={inputCls} value={selectedTemplateId || ""} onChange={e => handleTemplateSelect(e.target.value ? Number(e.target.value) : null)}>
                <option value="">No template</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}

          {/* Message content */}
          <div>
            <label className={labelCls}>Message <span className="text-red-400">*</span></label>
            <textarea className={`${inputCls} resize-none leading-relaxed`} rows={4}
              placeholder="Type your message here…" value={messageContent}
              onChange={e => setMessageContent(e.target.value)} required />
            <div className="flex items-center justify-between mt-1.5">
              <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden mr-3">
                <div className="h-full bg-[#0071BC] rounded-full transition-all" style={{ width: `${charPct}%` }} />
              </div>
              <span className="text-xs text-gray-400 whitespace-nowrap">{messageContent.length} chars · {segments} segment{segments !== 1 ? "s" : ""}</span>
            </div>
          </div>

          {/* Message type toggle */}
          <div>
            <label className={labelCls}>Message Type</label>
            <div className="flex bg-gray-100 rounded-2xl p-1 w-fit gap-1">
              {['sms','voice'].map(t => (
                <button key={t} type="button" onClick={() => setMessageType(t)}
                  className={`px-5 py-2 rounded-xl text-sm font-bold transition-all ${messageType === t ? 'bg-white text-[#0071BC] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                  {t.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Recipients */}
          <div>
            <label className={labelCls}>Recipients <span className="text-red-400">*</span></label>
            <div className="flex gap-2.5">
              <button type="button" onClick={() => { setContactSearch(''); setContactPickerOpen(true); }}
                className={`flex items-center gap-2 flex-1 justify-center py-3 rounded-2xl text-sm font-bold border-2 transition-all ${selectedContacts.length ? 'border-[#0071BC] bg-[#0071BC]/5 text-[#0071BC]' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                <FiUser size={14} />
                Contacts {selectedContacts.length > 0 && <span className="bg-[#0071BC] text-white text-xs rounded-full px-1.5 py-0.5">{selectedContacts.length}</span>}
              </button>
              <button type="button" onClick={() => { setGroupSearch(''); setGroupPickerOpen(true); }}
                className={`flex items-center gap-2 flex-1 justify-center py-3 rounded-2xl text-sm font-bold border-2 transition-all ${selectedGroups.length ? 'border-[#0071BC] bg-[#0071BC]/5 text-[#0071BC]' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                <FiUsers size={14} />
                Groups {selectedGroups.length > 0 && <span className="bg-[#0071BC] text-white text-xs rounded-full px-1.5 py-0.5">{selectedGroups.length}</span>}
              </button>
            </div>
          </div>

          {/* Schedule */}
          <div>
            <label className={labelCls}>Schedule <span className="text-gray-400 font-normal">(Optional)</span></label>
            <DatePicker selected={scheduledAt} onChange={(d: any) => setScheduledAt(d)}
              showTimeSelect timeFormat="HH:mm" timeIntervals={15}
              dateFormat="MMM d, yyyy h:mm aa" minDate={new Date()}
              placeholderText="Send immediately (or pick a time)"
              className={inputCls} />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={() => setCreateOpen(false)}
              className="flex-1 py-3.5 border border-gray-200 text-gray-500 text-sm font-bold rounded-2xl hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="submit"
              className="flex-1 py-3.5 bg-[#0071BC] text-white text-sm font-extrabold rounded-2xl hover:bg-[#005fa0] active:scale-[0.97] transition-all flex items-center justify-center gap-2">
              <FiSend size={14} /> {scheduledAt ? "Schedule Message" : "Send Message"}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Schedule Modal ────────────────────────────────────────── */}
      <Modal open={scheduleOpen} onClose={() => setScheduleOpen(false)} width="max-w-sm">
        <ModalHeader title="Schedule Message" subtitle="Pick a time to send" onClose={() => setScheduleOpen(false)} />
        <form onSubmit={handleSchedule} className="px-6 py-5 space-y-4">
          <div>
            <label className={labelCls}>Send At</label>
            <DatePicker selected={scheduledTime} onChange={(d: any) => setScheduledTime(d)}
              showTimeSelect timeFormat="HH:mm" timeIntervals={15}
              dateFormat="MMM d, yyyy h:mm aa" minDate={new Date()} required className={inputCls} />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => setScheduleOpen(false)}
              className="flex-1 py-3 border border-gray-200 text-gray-500 text-sm font-bold rounded-2xl hover:bg-gray-50 transition-colors">Cancel</button>
            <button type="submit"
              className="flex-1 py-3 bg-[#0071BC] text-white text-sm font-extrabold rounded-2xl hover:bg-[#005fa0] active:scale-[0.97] transition-all flex items-center justify-center gap-2">
              <FiCalendar size={14} /> Schedule
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Details Modal ─────────────────────────────────────────── */}
      <Modal open={detailsOpen} onClose={() => setDetailsOpen(false)} width="max-w-md">
        <ModalHeader title="Message Details" onClose={() => setDetailsOpen(false)} />
        {selectedMessage && (
          <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
            <div className="bg-gray-50 rounded-2xl p-4 space-y-3">
              {([
                ['Status', <StatusBadge status={selectedMessage.status} />],
                ['Sender ID', <span className="font-bold text-gray-800">{selectedMessage.sender?.sender_id}</span>],
                ['Type', <span className="capitalize font-medium">{selectedMessage.message_type}</span>],
                ['Recipients', `${selectedMessage.total_recipients}`],
                ['Cost', <span className="font-bold">₦{selectedMessage.cost}</span>],
                ['Delivery', selectedMessage.delivery_status || 'Pending'],
                ['Created', new Date(selectedMessage.created_at).toLocaleString()],
                ...(selectedMessage.scheduled_at ? [['Scheduled', new Date(selectedMessage.scheduled_at).toLocaleString()]] : []),
                ...(selectedMessage.sent_at ? [['Sent At', new Date(selectedMessage.sent_at).toLocaleString()]] : []),
              ] as [string, any][]).map(([label, val]) => (
                <div key={label} className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 font-medium">{label}</span>
                  <span className="text-right">{val}</span>
                </div>
              ))}
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
              <p className="text-xs font-bold text-gray-400 mb-1.5">MESSAGE CONTENT</p>
              <p className="text-sm text-gray-700 leading-relaxed">{selectedMessage.content}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {selectedMessage.status === 'draft' && <>
                <button onClick={() => { handleSend(selectedMessage.id); setDetailsOpen(false); }}
                  className="flex items-center gap-1.5 bg-green-500 text-white text-xs font-bold px-3 py-2 rounded-xl hover:bg-green-600 transition-colors">
                  <FiSend size={11} /> Send Now
                </button>
                <button onClick={() => { setDetailsOpen(false); setSelectedMessageId(selectedMessage.id); setScheduledTime(new Date()); setScheduleOpen(true); }}
                  className="flex items-center gap-1.5 bg-amber-500 text-white text-xs font-bold px-3 py-2 rounded-xl hover:bg-amber-600 transition-colors">
                  <FiCalendar size={11} /> Schedule
                </button>
              </>}
              {selectedMessage.status === 'scheduled' && (
                <button onClick={() => { handleCancel(selectedMessage.id); setDetailsOpen(false); }}
                  className="flex items-center gap-1.5 bg-orange-500 text-white text-xs font-bold px-3 py-2 rounded-xl hover:bg-orange-600 transition-colors">
                  <FiXCircle size={11} /> Cancel Schedule
                </button>
              )}
              {['draft','scheduled'].includes(selectedMessage.status) && (
                <button onClick={() => { setDetailsOpen(false); setDeleteOpen(true); }}
                  className="flex items-center gap-1.5 bg-red-500 text-white text-xs font-bold px-3 py-2 rounded-xl hover:bg-red-600 transition-colors">
                  <FiTrash2 size={11} /> Delete
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* ── Delete Confirm ────────────────────────────────────────── */}
      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} width="max-w-sm">
        <ModalHeader title="Delete Message" onClose={() => setDeleteOpen(false)} />
        <div className="px-6 py-5">
          <p className="text-sm text-gray-500 mb-5 leading-relaxed">This message will be permanently deleted. This action cannot be undone.</p>
          <div className="flex gap-3">
            <button onClick={() => setDeleteOpen(false)}
              className="flex-1 py-3 border border-gray-200 text-gray-500 text-sm font-bold rounded-2xl hover:bg-gray-50 transition-colors">Cancel</button>
            <button onClick={handleDelete}
              className="flex-1 py-3 bg-red-500 text-white text-sm font-extrabold rounded-2xl hover:bg-red-600 active:scale-[0.97] transition-all flex items-center justify-center gap-2">
              <FiTrash2 size={13} /> Delete
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Contact Picker ────────────────────────────────────────── */}
      <Modal open={contactPickerOpen} onClose={() => setContactPickerOpen(false)} width="max-w-md">
        <ModalHeader title="Select Contacts" subtitle={`${selectedContacts.length} selected`} onClose={() => setContactPickerOpen(false)} />
        <div className="px-6 pt-4 pb-2 flex-shrink-0">
          <div className="relative">
            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
            <input type="text" className={`${inputCls} pl-10 py-2.5`} placeholder="Search contacts…"
              value={contactSearch} onChange={e => setContactSearch(e.target.value)} />
          </div>
        </div>
        <div className="overflow-y-auto flex-1 px-6 pb-4 space-y-1 mt-2">
          {loadingRefs ? (
            <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-gray-200 border-t-[#0071BC] rounded-full animate-spin" /></div>
          ) : filteredContacts.length === 0 ? (
            <p className="text-center text-gray-400 py-10 text-sm">No contacts found</p>
          ) : filteredContacts.map(c => (
            <button key={c.id} type="button" onClick={() => toggleContact(c.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-left transition-all ${selectedContacts.includes(c.id) ? 'bg-[#0071BC]/8 border border-[#0071BC]/20' : 'hover:bg-gray-50 border border-transparent'}`}>
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 text-xs font-bold ${selectedContacts.includes(c.id) ? 'bg-[#0071BC] text-white' : 'bg-gray-100 text-gray-500'}`}>
                {selectedContacts.includes(c.id) ? <FiCheck size={13} strokeWidth={3} /> : (c.first_name?.[0] || '?').toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800">{c.first_name} {c.last_name}</p>
                <p className="text-xs text-gray-400">{c.phone}</p>
              </div>
            </button>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3 flex-shrink-0">
          <button onClick={() => setContactPickerOpen(false)} className="flex-1 py-3 border border-gray-200 text-gray-500 text-sm font-bold rounded-2xl hover:bg-gray-50 transition-colors">Cancel</button>
          <button onClick={() => setContactPickerOpen(false)} className="flex-1 py-3 bg-[#0071BC] text-white text-sm font-extrabold rounded-2xl hover:bg-[#005fa0] active:scale-[0.97] transition-all">
            Done · {selectedContacts.length} selected
          </button>
        </div>
      </Modal>

      {/* ── Group Picker ──────────────────────────────────────────── */}
      <Modal open={groupPickerOpen} onClose={() => setGroupPickerOpen(false)} width="max-w-md">
        <ModalHeader title="Select Groups" subtitle={`${selectedGroups.length} selected`} onClose={() => setGroupPickerOpen(false)} />
        <div className="px-6 pt-4 pb-2 flex-shrink-0">
          <div className="relative">
            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
            <input type="text" className={`${inputCls} pl-10 py-2.5`} placeholder="Search groups…"
              value={groupSearch} onChange={e => setGroupSearch(e.target.value)} />
          </div>
        </div>
        <div className="overflow-y-auto flex-1 px-6 pb-4 space-y-1 mt-2">
          {loadingRefs ? (
            <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-gray-200 border-t-[#0071BC] rounded-full animate-spin" /></div>
          ) : filteredGroups.length === 0 ? (
            <p className="text-center text-gray-400 py-10 text-sm">No groups found</p>
          ) : filteredGroups.map(g => (
            <button key={g.id} type="button" onClick={() => toggleGroup(g.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-left transition-all ${selectedGroups.includes(g.id) ? 'bg-[#0071BC]/8 border border-[#0071BC]/20' : 'hover:bg-gray-50 border border-transparent'}`}>
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${selectedGroups.includes(g.id) ? 'bg-[#0071BC] text-white' : 'bg-gray-100 text-gray-500'}`}>
                {selectedGroups.includes(g.id) ? <FiCheck size={13} strokeWidth={3} /> : <FiUsers size={13} />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800">{g.name}</p>
                <p className="text-xs text-gray-400">{g.contact_count} contacts</p>
              </div>
            </button>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3 flex-shrink-0">
          <button onClick={() => setGroupPickerOpen(false)} className="flex-1 py-3 border border-gray-200 text-gray-500 text-sm font-bold rounded-2xl hover:bg-gray-50 transition-colors">Cancel</button>
          <button onClick={() => setGroupPickerOpen(false)} className="flex-1 py-3 bg-[#0071BC] text-white text-sm font-extrabold rounded-2xl hover:bg-[#005fa0] active:scale-[0.97] transition-all">
            Done · {selectedGroups.length} selected
          </button>
        </div>
      </Modal>

      <ToastContainer position="bottom-right" autoClose={2500} hideProgressBar theme="light" toastStyle={{ borderRadius: "12px" }} />
    </>
  );
}
